<script setup lang="ts">
// Экран ссылки `/join/<токен>`: сначала спрашивает у узла, ЧТО это за ссылка, и только потом
// предлагает действие.
//
// Раньше он считал приглашением сотрудника любую ссылку — и внешнему человеку, которого позвали на
// встречу, предлагал придумать пароль и завести учётную запись в чужой компании. Это витрина
// продукта: по такой ссылке приходит подрядчик или клиент заказчика.
import { ErrorReason } from "@yofis/proto/yofis/v1/errors_pb";
import { LinkScope, type LinkPreview } from "@yofis/proto/yofis/v1/link_pb";
import { errorReason } from "@yofis/core";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useSession } from "../stores/session.ts";

const session = useSession();
const router = useRouter();
const route = useRoute();

const token = String(route.params["token"] ?? "");
const preview = ref<LinkPreview | undefined>(undefined);
const loading = ref(true);
const refusal = ref("");
const displayName = ref("");
const password = ref("");
const showPassword = ref(false);
/** Вошли по ссылке в чат с лобби: решение принимает участник чата, а мы ждём. */
const stageWaiting = ref(false);

onMounted(async () => {
  await session.loadNode();
  try {
    preview.value = await session.resolveLink(token);
    // Вошедшему человеку ссылка в чат не должна предлагать заводить учётную запись заново: у него
    // уже есть сессия, и от него требуется только согласие войти — оно и есть переход по ссылке.
    const scopeNow = preview.value?.scope;
    const chatInvite = scopeNow === LinkScope.CHAT_INVITE || scopeNow === LinkScope.CHANNEL_INVITE;
    if (chatInvite && session.state !== undefined && session.client !== undefined) {
      const entered = await session.client.joinByLink(token);
      if (entered.lobbyPending) {
        stageWaiting.value = true;
      } else if (entered.chat !== undefined) {
        await router.replace({ name: "chat", params: { id: entered.chat.id } });
      }
      return;
    }
    // Причина приходит прямо в превью (0.18) — по ней и отказываем, не заставляя человека жать
    // кнопку на заведомо мёртвой ссылке. По полю `expired` отказывать нельзя: оно одно на три
    // случая, а следующий шаг у них разный — просить новую ссылку или спрашивать, КТО вошёл по
    // твоей. Именно на этом я и ошиблась, пока причины в превью не было.
    const reason = preview.value?.reason ?? ErrorReason.UNSPECIFIED;
    if (reason !== ErrorReason.UNSPECIFIED) {
      refusal.value = ErrorReason[reason] ?? "UNKNOWN";
    }
  } catch (err) {
    refusal.value = errorReason(err) ?? "UNKNOWN";
  } finally {
    loading.value = false;
  }
});

const scope = computed(() => preview.value?.scope ?? LinkScope.UNSPECIFIED);
const title = computed(() => preview.value?.title ?? "");

/** Четыре отказа различимы: у каждого свой следующий шаг, а «ссылка не работает» его не подсказывает. */
const refusalText = computed(() => {
  switch (refusal.value) {
    case "LINK_EXPIRED":
      return { head: "Срок ссылки истёк", body: "Попросите новую у того, кто её прислал." };
    case "LINK_REVOKED":
      return { head: "Ссылка отозвана", body: "Её отключили. Попросите новую у того, кто её прислал." };
    case "LINK_EXHAUSTED":
      return {
        head: "Ссылка уже использована",
        body: "По ней уже вошли. Если это были не вы — скажите администратору узла.",
      };
    case "GUESTS_DISABLED":
      return {
        head: "Узел не принимает внешних участников",
        body: "Это настройка компании, а не ошибка с вашей стороны. Попросите пригласившего открыть встречу для внешних.",
      };
    default:
      return { head: "Ссылка не открывается", body: "Попробуйте позже или попросите новую." };
  }
});

async function act(): Promise<void> {
  refusal.value = "";
  try {
    if (scope.value === LinkScope.ROOM_JOIN) {
      await session.joinAsGuest(token, displayName.value);
      if (session.state !== undefined) {
        await router.replace({ name: "room", params: { token } });
      } else if (session.reason !== "") {
        refusal.value = session.reason;
      }
      return;
    }
    if (scope.value === LinkScope.LOGIN) {
      await session.loginByLink(token);
    } else {
      await session.join(token, displayName.value, showPassword.value ? password.value : "");
    }
    if (session.state !== undefined) {
      await router.replace("/");
      return;
    }
    // Хранилище ловит отказ у себя и называет причину: показываем её экраном, а не строкой ошибки.
    if (session.reason !== "") {
      refusal.value = session.reason;
    }
  } catch (err) {
    refusal.value = errorReason(err) ?? "UNKNOWN";
  }
}
</script>

<template>
  <div class="y-center">
    <div v-if="loading" class="y-auth y-stack">
      <div class="y-auth-sub">Открываем ссылку…</div>
    </div>

    <!-- Заявка в лобби чата: человек не внутри, и говорить обратное нельзя. -->
    <div v-else-if="stageWaiting" class="y-auth y-stack">
      <div>
        <div class="y-auth-title">{{ title || "Заявка отправлена" }}</div>
        <div class="y-auth-sub">Вас впустят, когда участник чата подтвердит. Экран обновится сам.</div>
      </div>
    </div>

    <!-- Отказ: человек должен понять, что делать дальше, а не гадать. -->
    <div v-else-if="refusal" class="y-auth y-stack">
      <div>
        <div class="y-auth-title">{{ refusalText.head }}</div>
        <div class="y-auth-sub">{{ refusalText.body }}</div>
      </div>
      <button class="y-btn lg" @click="router.replace('/')">На главную</button>
    </div>

    <!-- Встреча: внешнему участнику нужно только имя. Ни пароля, ни учётной записи. -->
    <form v-else-if="scope === LinkScope.ROOM_JOIN" class="y-auth y-stack" @submit.prevent="act">
      <div>
        <div class="y-auth-title">{{ title || "Встреча" }}</div>
        <div class="y-auth-sub">
          {{ preview?.requiresAccount
            ? "Для входа нужна учётная запись на этом узле"
            : "Вас пригласили на встречу. Учётная запись не нужна — представьтесь, и всё." }}
        </div>
      </div>
      <label class="y-field">
        <input v-model="displayName" class="y-input" name="name" autocomplete="name" placeholder="Как вас зовут">
      </label>
      <div v-if="session.error" class="y-auth-err">{{ session.error }}</div>
      <button class="y-btn primary lg" type="submit" :disabled="session.busy || displayName.trim() === ''">
        {{ session.busy ? "Входим…" : "Войти во встречу" }}
      </button>
      <div v-if="preview?.requiresLobby" class="y-auth-sub">Вас впустят, когда организатор подтвердит.</div>
    </form>

    <!-- Вход сотрудника по ссылке: без пароля и без формы регистрации. -->
    <form v-else-if="scope === LinkScope.LOGIN" class="y-auth y-stack" @submit.prevent="act">
      <div>
        <div class="y-auth-title">{{ session.node?.displayName || "Yofis" }}</div>
        <div class="y-auth-sub">Вход по ссылке{{ title ? ` — ${title}` : "" }}</div>
      </div>
      <div v-if="session.error" class="y-auth-err">{{ session.error }}</div>
      <button class="y-btn primary lg" type="submit" :disabled="session.busy">
        {{ session.busy ? "Входим…" : "Войти" }}
      </button>
    </form>

    <!-- Приглашение сотрудника: пароль необязателен (0.15), поэтому он спрятан за ссылкой. -->
    <form v-else class="y-auth y-stack" @submit.prevent="act">
      <div>
        <div class="y-auth-title">{{ session.node?.displayName || "Yofis" }}</div>
        <div class="y-auth-sub">
          {{ scope === LinkScope.USER_INVITE ? `Приглашение в ${session.node?.domain ?? ""}` : title || "Приглашение" }}
        </div>
      </div>
      <label class="y-field">
        <input v-model="displayName" class="y-input" name="name" autocomplete="name" placeholder="Как вас зовут">
      </label>
      <label v-if="showPassword" class="y-field">
        <input v-model="password" class="y-input" name="password" type="password" autocomplete="new-password" placeholder="Придумайте пароль">
      </label>
      <button v-else type="button" class="y-btn" @click="showPassword = true">Задать пароль (необязательно)</button>
      <div v-if="session.error" class="y-auth-err">{{ session.error }}</div>
      <button class="y-btn primary lg" type="submit" :disabled="session.busy || displayName.trim() === ''">
        {{ session.busy ? "Входим…" : "Войти" }}
      </button>
    </form>
  </div>
</template>
